/**
 * ScenarioEditorHeader
 *
 * Header component for the ScenarioEditor containing:
 * - Breadcrumb navigation
 * - Scenario title with edit button
 * - Save status indicator
 * - Save button
 * - Optimization engine toggle
 * - Run optimization button
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Breadcrumbs,
} from '@mui/material';
import {
    Save as SaveIcon,
    Edit as EditIcon,
    ArrowBack,
} from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { SaveStatusIndicator } from '../../../components/SaveStatusIndicator';

/**
 * @param {Object} props
 * @param {boolean} props.isRetro - Whether retro theme mode is active
 * @param {string} props.projectName - Current project name for breadcrumb
 * @param {string} props.scenarioName - Current scenario name
 * @param {Function} props.onNavigateBack - Callback to navigate back to projects
 * @param {Function} props.onRename - Callback to open rename dialog
 * @param {boolean} props.isDirty - Whether form has unsaved changes
 * @param {Object} props.saveStatus - Save status from optimistic sync
 * @param {string} props.saveError - Save error message if any
 * @param {Date} props.lastSavedAt - Timestamp of last save
 * @param {Function} props.onSave - Callback to save scenario
 * @param {string} props.optimizationEngine - Current optimization engine ('nrel_reopt' or 'vortx_flux')
 * @param {Function} props.onEngineChange - Callback when engine changes
 * @param {Function} props.onSubmit - Callback to submit optimization
 * @param {boolean} props.isSubmitting - Whether optimization is being submitted
 * @param {Array} props.missingFields - Array of missing required fields
 * @param {Object} props.vortxStyles - Vortx styling object
 */
export default function ScenarioEditorHeader({
    isRetro,
    projectName,
    scenarioName,
    onNavigateBack,
    onRename,
    isDirty,
    saveStatus,
    saveError,
    lastSavedAt,
    onSave,
    optimizationEngine,
    onEngineChange,
    onSubmit,
    isSubmitting,
    missingFields,
    vortxStyles,
}) {
    return (
        <>
            {/* Breadcrumb Navigation - hide in retro mode (header has breadcrumb) */}
            {!isRetro && (
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link to="/projects" style={{ color: vortxColors.gold, textDecoration: 'none' }}>
                        Projects
                    </Link>
                    <Typography color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {projectName || 'Project'} / {scenarioName || 'New Scenario'}
                    </Typography>
                </Breadcrumbs>
            )}

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={onNavigateBack}
                        sx={isRetro ? { color: vortxColors.gold } : {}}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Typography
                        variant={isRetro ? undefined : "h4"}
                        sx={isRetro ? {
                            fontFamily: vortxTypography.heading,
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: vortxColors.gold,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            textShadow: `0 0 15px rgba(178, 140, 80, 0.3)`,
                        } : {
                            fontFamily: vortxTypography.heading,
                            color: 'text.primary',
                            fontWeight: 700
                        }}
                    >
                        {scenarioName || 'New Scenario'}
                    </Typography>
                    <IconButton
                        onClick={onRename}
                        sx={isRetro ? { color: '#666', borderRadius: 0, '&:hover': { color: vortxColors.gold } } : {}}
                    >
                        <EditIcon />
                    </IconButton>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Save Status Indicator */}
                    <SaveStatusIndicator
                        status={isDirty ? 'dirty' : saveStatus}
                        error={saveError}
                        lastSavedAt={lastSavedAt}
                    />

                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={onSave}
                        className="vx-pressable"
                        sx={{ borderColor: vortxColors.gold, color: vortxColors.gold }}
                    >
                        Save
                    </Button>

                    {/* Optimization Engine Toggle */}
                    <ToggleButtonGroup
                        value={optimizationEngine}
                        exclusive
                        onChange={(e, val) => val && onEngineChange(val)}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: 'text.primary',
                                borderColor: vortxColors.gold,
                                fontSize: '0.75rem',
                                px: 1.5,
                                py: 0.5,
                                '&.Mui-selected': {
                                    bgcolor: vortxColors.gold,
                                    color: vortxColors.darkGreen,
                                    fontWeight: 600,
                                    '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.9)' }
                                },
                                '&:hover': { bgcolor: 'rgba(178, 140, 80, 0.1)' }
                            }
                        }}
                    >
                        <ToggleButton value="nrel_reopt">NREL REopt</ToggleButton>
                        <ToggleButton value="vortx_flux">Vortx Flux</ToggleButton>
                    </ToggleButtonGroup>

                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        disabled={isSubmitting || missingFields.length > 0}
                        className="vx-pressable"
                        sx={vortxStyles.button}
                        title={missingFields.length > 0 ? `Missing: ${missingFields.map(f => f.label).join(', ')}` : ''}
                    >
                        {isSubmitting ? 'Submitting...' : missingFields.length > 0 ? 'Complete Required Fields' : 'Run Optimization'}
                    </Button>
                </Box>
            </Box>
        </>
    );
}
